import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Lock, ArrowRight, BookOpen, Zap, Cpu, ChevronDown, ChevronUp } from 'lucide-react';
import { pythonRoadmap } from '../../data';

const completedDays = new Set<number>();

const weekColors = ['#f59e0b', '#00e5c8', '#8b5cf6', '#10b981', '#f43f5e', '#00e5c8', '#f59e0b', '#10b981', '#8b5cf6', '#f43f5e'];

const lessonPreviews: Record<string, { what: string; mental: string; code: string; takeaway: string }> = {
  'python-internals': {
    what: 'Python\'s memory model uses reference counting + cyclic garbage collector. Every object has a reference count; when it hits zero, the memory is freed immediately.',
    mental: 'Think of each Python object as a box with a number on it. Every time someone points to it, the number goes up. When the number hits 0, Python throws the box away. The GC handles the case where two boxes point to each other.',
    code: `import sys
import gc

x = []
print(sys.getrefcount(x))  # 2 (x + argument to getrefcount)

y = x
print(sys.getrefcount(x))  # 3 (x, y, + argument)

del y
print(sys.getrefcount(x))  # 2 again

# Cycle detection
a = []
b = [a]
a.append(b)
del a, b  # ref counts > 0 but unreachable — GC handles this`,
    takeaway: 'Understanding refcounting explains why __del__ is unreliable, why circular imports can cause issues, and how to optimize memory in long-running services.',
  },
  'fastapi-intro': {
    what: 'FastAPI is an ASGI framework built on Starlette and Pydantic. It auto-generates OpenAPI docs, validates requests/responses via type hints, and is fully async-native.',
    mental: 'FastAPI turns your Python type annotations into a schema. Pydantic validates at the boundary; Starlette handles the HTTP plumbing. Your job is pure business logic.',
    code: `from fastapi import FastAPI, HTTPException, Depends
from pydantic import BaseModel
from typing import Annotated

app = FastAPI()

class UserCreate(BaseModel):
    name: str
    email: str
    age: int  # Pydantic validates this is actually an int

@app.post("/users", status_code=201)
async def create_user(user: UserCreate) -> dict:
    # user is already validated — no manual parsing
    return {"id": "abc123", **user.model_dump()}`,
    takeaway: 'Never write manual validation. Let Pydantic own the boundary — your endpoints should assume valid data and focus on business logic.',
  },
  'llm-fundamentals': {
    what: 'LLMs are transformer-based models that predict the next token given previous tokens. They\'re stateless — each API call is independent. "Memory" is just tokens in the context window.',
    mental: 'An LLM is a very sophisticated autocomplete that was trained on the internet. It doesn\'t "think" — it samples from a probability distribution over tokens. Temperature controls how random that sampling is.',
    code: `from anthropic import Anthropic

client = Anthropic()

# The model has NO memory between calls
# Everything it "knows" about your conversation is in messages[]
response = client.messages.create(
    model="claude-opus-4-5",
    max_tokens=1000,
    system="You are a helpful Python tutor.",
    messages=[
        {"role": "user", "content": "Explain generators in Python"}
    ]
)

print(response.content[0].text)`,
    takeaway: 'Context is everything. The quality of your LLM output is a direct function of what you put in the context window — not magic.',
  },
};

function TopicRow({ topic, weekColor }: { topic: any; weekColor: string }) {
  const [expanded, setExpanded] = useState(false);
  const isCompleted = completedDays.has(topic.day);
  const preview = lessonPreviews[topic.slug];

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div
        onClick={() => preview && setExpanded(e => !e)}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
          cursor: preview ? 'pointer' : 'default',
          transition: 'background 0.15s',
          background: expanded ? 'var(--surface)' : 'transparent',
        }}
        onMouseOver={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.02)'; }}
        onMouseOut={e => { if (!expanded) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
      >
        {isCompleted
          ? <CheckCircle2 size={16} style={{ color: weekColor, flexShrink: 0 }} />
          : <Lock size={15} style={{ color: 'var(--text3)', flexShrink: 0 }} />
        }
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', minWidth: 40, flexShrink: 0 }}>Day {topic.day}</span>
        <span style={{ fontSize: 14, color: isCompleted ? 'var(--text)' : 'var(--text2)', flex: 1 }}>{topic.title}</span>
        {topic.status === 'coming-soon' && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text3)' }}>Soon</span>}
        {preview && (expanded ? <ChevronUp size={14} style={{ color: 'var(--text3)' }} /> : <ChevronDown size={14} style={{ color: 'var(--text3)' }} />)}
      </div>

      {expanded && preview && (
        <div style={{ padding: '0 20px 20px 50px', borderTop: '1px solid var(--border)' }}>
          <div style={{ marginTop: 20, display: 'grid', gap: 14 }}>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: weekColor, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>What it is</p>
              <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7 }}>{preview.what}</p>
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: 18 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Mental Model</p>
              <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.7 }}>{preview.mental}</p>
            </div>
            <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 10, padding: 18 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>Code Example</p>
              <pre style={{ margin: 0, overflow: 'auto' }}><code style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text2)', lineHeight: 1.7 }}>{preview.code}</code></pre>
            </div>
            <div style={{ borderLeft: `3px solid ${weekColor}`, paddingLeft: 14 }}>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: weekColor, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Key Takeaway</p>
              <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, fontWeight: 500 }}>{preview.takeaway}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function WeekCard({ week, index }: { week: any; index: number }) {
  const [open, setOpen] = useState(index === 0);
  const color = weekColors[index] || '#00e5c8';
  const completedCount = week.topics.filter((t: any) => completedDays.has(t.day)).length;
  const pct = Math.round((completedCount / week.topics.length) * 100);

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: '22px 24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Week {week.id} · {week.topics.length} days
            </span>
            {pct === 100 && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '2px 8px', borderRadius: 4 }}>Complete</span>}
          </div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.01em', marginBottom: 6 }}>{week.title}</h3>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {week.description.split(',').map((t: string) => (
              <span key={t} style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', background: 'var(--surface)', padding: '2px 8px', borderRadius: 4 }}>{t.trim()}</span>
            ))}
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: pct > 0 ? color : 'var(--text3)' }}>{pct}%</span>
          <p style={{ color: 'var(--text3)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>{completedCount}/{week.topics.length}</p>
        </div>
      </div>
      <div style={{ height: 2, background: 'var(--border)', margin: '0 24px' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 1, transition: 'width 0.5s ease' }} />
      </div>
      {open && (
        <div style={{ marginTop: 8 }}>
          {week.topics.map((topic: any) => (
            <TopicRow key={topic.day} topic={topic} weekColor={color} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function PythonTutorial() {
  const totalDays = pythonRoadmap.totalDays;

  return (
    <div style={{ paddingTop: 64, minHeight: '100vh' }}>
      {/* Hero */}
      <section style={{ padding: '72px 24px 48px', position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', top: -80, right: '20%', width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,158,11,0.07) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div className="badge" style={{ marginBottom: 20 }}>
            <Cpu size={11} /> Python · AI · Backend Guide
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 64px)',
            fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 16,
          }}>
            Python Backend &<br />
            <span style={{ color: '#f59e0b' }}>AI Engineering</span>
          </h1>
          <p style={{ color: 'var(--text2)', fontSize: 18, maxWidth: 580, lineHeight: 1.7, marginBottom: 32 }}>
            A 10-week journey from Python internals to production AI agents. FastAPI, RAG systems, LLM orchestration — built toward founding engineer level.
          </p>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Total Days', value: totalDays },
              { label: 'Weeks', value: 10 },
              { label: 'Projects', value: '5+' },
              { label: 'Level', value: 'Sr → Founding' },
            ].map(s => (
              <div key={s.label} style={{
                padding: '14px 22px', background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)', textAlign: 'center',
              }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 800, color: '#f59e0b' }}>{s.value}</div>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 2 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you'll build */}
      <section style={{ padding: '0 24px 48px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{
            padding: 28, background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24,
          }}>
            {[
              { icon: <BookOpen size={18} />, title: 'Foundation First', desc: 'Python internals, type system, async patterns — before any framework.' },
              { icon: <Zap size={18} />, title: 'AI-Native Stack', desc: 'FastAPI + Pydantic + RAG + Agents. The stack that gets hired in 2026.' },
              { icon: <Cpu size={18} />, title: 'Production Ready', desc: 'Redis, Celery, Docker, CI/CD, observability — not just tutorials, real deployment.' },
            ].map(item => (
              <div key={item.title} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }}>{item.icon}</div>
                <div>
                  <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{item.title}</p>
                  <p style={{ color: 'var(--text2)', fontSize: 13, lineHeight: 1.6 }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10-Week Curriculum */}
      <section style={{ padding: '0 24px 80px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text3)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 24 }}>
            10-Week Curriculum · Click any week to expand
          </p>
          <div style={{ display: 'grid', gap: 16 }}>
            {pythonRoadmap.weeks.map((week, i) => (
              <WeekCard key={week.id} week={week} index={i} />
            ))}
          </div>

          {/* Skill progression */}
          <div style={{ marginTop: 48, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            {[
              { label: 'Week 1–2', desc: 'Python mastery', color: '#f59e0b' },
              { label: 'Week 3–6', desc: 'AI/API engineering', color: '#00e5c8' },
              { label: 'Week 7–8', desc: 'Systems design', color: '#8b5cf6' },
              { label: 'Week 9–10', desc: 'Founding engineer', color: '#f43f5e' },
            ].map(step => (
              <div key={step.label} style={{
                padding: '18px 20px', background: 'var(--surface)', border: `1px solid ${step.color}30`,
                borderRadius: 'var(--radius-sm)', borderTop: `3px solid ${step.color}`,
              }}>
                <p style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: step.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{step.label}</p>
                <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16 }}>{step.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{
            marginTop: 48, padding: 32,
            background: 'linear-gradient(135deg, rgba(245,158,11,0.07), rgba(239,68,68,0.07))',
            border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius)', textAlign: 'center',
          }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, marginBottom: 10 }}>Follow the Journey</h3>
            <p style={{ color: 'var(--text2)', fontSize: 15, marginBottom: 24, maxWidth: 460, margin: '0 auto 24px' }}>
              Each week I ship articles breaking down the concepts, with code and real examples from my own work.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/blog" style={{
                padding: '10px 22px', background: '#f59e0b', color: '#080b10',
                borderRadius: 10, fontWeight: 600, fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 6,
              }}>Read the Blog <ArrowRight size={14} /></Link>
              <Link to="/react" style={{
                padding: '10px 22px', background: 'transparent', color: 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 10, fontWeight: 500, fontSize: 14,
              }}>React Guide →</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
